<template>
  <div class="filter-wrapper">
    <div class="filter">
      <div class="filter-header">
        <div class="filter-header-title-section">
          <div class="rich-text">
            <h5>Apply Filter</h5>
          </div>
          <div class="filter-header-title-section--icon" @click="onClose">
            <icon name="times" size="24" />
          </div>
        </div>
        <div class="filter-header-save-section">
          <div class="rich-text">
            <span class="small">{{
              currentFilter.name || "Custom Filter"
            }}</span>
            <span v-if="currentFilter.isEdited" class="small edited">
              - Edited
            </span>
          </div>
          <Button
            v-if="currentFilter.isEdited"
            label="Save as"
            small
            @onClick="onSave" />
        </div>
        <text-input
          id="filter-search-input"
          v-model="searchTerm"
          class="filter-header-search-section"
          :show-clear-btn="true"
          :value="searchTerm"
          placeholder="Search for filter"
          icon="magnifying-glass"
          icon-position="left"
          @onChange="onSearch" />
      </div>
      <div class="filter-body">
        <div v-for="(fieldMapping, key) in fieldMappings" :key="key">
          <checkbox-filter
            v-if="fieldMapping.type === 'checkbox' && fieldMapping.isVisible"
            :title="key"
            :expanded="fieldMapping.expanded"
            :loading="fieldMapping.isLoading"
            :field-data="fieldMapping"
            :options="facets[fieldMapping.fieldName] || []"
            :values="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].values
                : []
            "
            @onSearchUpdateFacetIfNotFound="onSearchUpdateFacetIfNotFound"
            @onClearFacet="onClearFacet"
            @onCardToggle="onCardToggle" />
          <date-filter
            v-if="fieldMapping.type === 'date' && fieldMapping.isVisible"
            :title="key"
            :expanded="fieldMapping.expanded"
            :field-data="fieldMapping"
            :options="facets[fieldMapping.fieldName]"
            :from-date="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].from
                : ''
            "
            :to-date="
              filterData[key] && filterData[key][0] ? filterData[key][0].to : ''
            "
            :relative-date="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].relative_date
                : ''
            "
            :from-days="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].from_days
                : ''
            "
            :to-days="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].to_days
                : ''
            "
            :filter-type="
              filterData[key] && filterData[key][0]
                ? filterData[key][0].type
                : ''
            "
            @onCardToggle="onCardToggle" />
        </div>
      </div>
      <div class="filter-footer">
        <div v-if="showShareButton" class="filter-footer__share-block">
          <Button
            label="Share Filter"
            appearance="tertiary"
            icon-position="left"
            icon="share"
            transparent
            full-width
            @onClick="openShareFilterModal" />
        </div>
        <div class="filter-footer__tertiary-block">
          <Button
            label="Configure filters"
            appearance="tertiary"
            icon-position="left"
            icon="sliders-horizontal"
            transparent
            @onClick="toggleConfigFilterModal" />
          <Button
            label="Saved Filters"
            appearance="tertiary"
            icon-position="left"
            icon="floppy-disk"
            transparent
            @onClick="toggleSavedFilterModal" />
        </div>
        <div v-if="showApplyButton" class="filter-footer__save-block">
          <Button label="Apply Filter" appearance="primary" full-width />
        </div>
        <div class="filter-footer__clear-block" @click="onClear">
          <span>Clear Filter</span>
        </div>
      </div>
    </div>
    <configure-filter-modal
      v-if="configFilterModalIsOpen"
      :configuration="configuration.fieldMappings"
      :default-columns="defaultColumns"
      :cache-name="cacheName"
      @onModalClose="toggleConfigFilterModal"
      @onSaveColumnConfig="onSaveColumnConfig" />
    <saved-filter-modal
      v-if="savedFilterModalIsOpen"
      :cache-name="cacheName"
      :is-loading="isSavedFiltersLoading"
      :saved-filters="savedFilters"
      @onModalClose="toggleSavedFilterModal" />
    <save-new-filter-modal
      v-if="saveNewFilterModalIsOpen"
      :cache-name="cacheName"
      :is-loading="isSavedFiltersLoading"
      :is-update="!!currentFilter.name"
      :current-filter="currentFilter.name"
      :saved-filters="savedFilters"
      @onModalClose="toggleSaveNewFilterModal" />
  </div>
</template>

<script>
//Common components
import CheckboxFilter from "./CheckboxFilter";
import DateFilter from "./DateFilter";
import ConfigureFilterModal from "./ConfigureFilterModal";
import SavedFilterModal from "./SavedFilterModal";
import SaveNewFilterModal from "./SaveNewFilterModal";

export default {
  name: "FilterBar",
  components: {
    CheckboxFilter,
    ConfigureFilterModal,
    DateFilter,
    SavedFilterModal,
    SaveNewFilterModal,
  },
  props: {
    iconOnly: { type: Boolean, default: false },
    configuration: { type: Object, required: true },
    defaultColumns: { type: Array, required: true },
    facets: { type: Object, required: true },
    filterData: { type: Object, required: true },
    showApplyButton: { type: Boolean, default: false },
    showShareButton: { type: Boolean, default: false },
    cacheName: { type: String, default: "" },
    savedFilters: { type: Array, default: () => {} },
    currentFilter: { type: Object, required: true },
    isSavedFiltersLoading: { type: Boolean, default: true },
  },
  data() {
    return {
      configFilterModalIsOpen: false,
      savedFilterModalIsOpen: false,
      saveNewFilterModalIsOpen: false,
      isExistingFilter: false,
      fieldMappings: this.configuration.fieldMappings,
    };
  },
  methods: {
    onClose() {
      this.$emit("onClose");
    },
    onCardToggle(cardName) {
      this.$emit("onCardToggle", cardName);
    },
    toggleConfigFilterModal() {
      this.$emit("onCloseShareFilterModal");
      this.configFilterModalIsOpen = !this.configFilterModalIsOpen;
    },
    toggleSavedFilterModal() {
      this.savedFilterModalIsOpen = !this.savedFilterModalIsOpen;
      if (this.savedFilterModalIsOpen) {
        this.$emit("onSavedFilterModalOpen");
      }
    },
    toggleSaveNewFilterModal() {
      this.saveNewFilterModalIsOpen = !this.saveNewFilterModalIsOpen;
    },
    onSaveNewFilterSuccess() {
      this.saveNewFilterModalIsOpen = false;
    },
    onSaveColumnConfig(configuration) {
      this.$emit("onSaveColumnConfig", configuration);
    },
    onClear() {
      this.$emit("onFilterClear");
    },
    onSave() {
      this.saveNewFilterModalIsOpen = true;
      this.$emit("onSavedFilterModalOpen");
    },
    openShareFilterModal() {
      this.$emit("onShareFilterModal");
    },
    onSearchUpdateFacetIfNotFound(data) {
      this.$emit("onSearchUpdateFacetIfNotFound", data);
    },
    onClearFacet(data) {
      this.$emit("onClearFacet", data);
    },
    onSearch(e) {
      let searchTerm = e.target.value;
      let searchedFieldMappings = {};
      if (searchTerm.length) {
        searchTerm = searchTerm.toLowerCase().trim().split(" ").join("");
        Object.keys(this.configuration.fieldMappings).forEach((item) => {
          if (
            item.toLowerCase().trim().split(" ").join("").includes(searchTerm)
          ) {
            searchedFieldMappings[item] =
              this.configuration.fieldMappings[item];
          }
        });
        this.fieldMappings = searchedFieldMappings;
      } else {
        this.fieldMappings = this.configuration.fieldMappings;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-wrapper {
  position: relative;
  width: 480px;
  transition: all 0.5s ease;
  & .filter {
    position: fixed;
    top: 0;
    z-index: 999;
    background: var(--Gray-100);
    overflow-y: hidden;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 24px;
    width: inherit;
    & .filter-header {
      & .rich-text h5 {
        background: var(--Gray-100);
        color: var(--Blue-300-Maersk);
      }
      & .filter-header-title-section {
        justify-content: space-between;
        display: flex;
        & .filter-header-title-section--icon {
          cursor: pointer;
        }
      }
      & .filter-header-save-section {
        display: flex;
        margin: 8px 0;
        align-items: center;
        & .rich-text {
          & span {
            color: var(--Gray-900-Black);
            &.edited {
              color: var(--Gray-600);
              margin-left: 4px;
            }
          }
          & a {
            font-size: 12px;
            margin-left: 16px;
            font-weight: normal;
          }
        }
        & a.button {
          margin-left: 16px;
        }
      }
      & .filter-header-search-section {
        margin-bottom: 4px;
      }
    }
    & .filter-body {
      display: flex;
      flex: 1;
      flex-direction: column;
      overflow-y: scroll;
      &--loader {
        z-index: 1;
      }
    }
    & .filter-footer {
      margin-top: 16px;
      & .filter-footer__tertiary-block {
        display: flex;
        justify-content: space-between;
        & .button {
          width: 48%;
          text-align: center;
        }
      }
      & .filter-footer__save-block {
        display: flex;
        margin: 16px 0;
      }
      & .filter-footer__share-block {
        display: flex;
        margin: 16px 0;
      }
      & .filter-footer__clear-block {
        margin-top: 16px;
        text-align: center;
        & span {
          color: var(--Blue-600-Dark);
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
